/**
 * Interceptador Axios para gerenciamento automático de tokens JWT
 * Implementa refresh automático e retry de requisições
 */

import axios, { AxiosError, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { authStorage, AuthTokens } from './authStorage';

// Estado interno para controlar refresh
let isRefreshingToken = false;
let failedQueue: Array<{
  resolve: (value: string) => void;
  reject: (error: any) => void;
}> = [];

/**
 * Processa a fila de requisições pendentes após refresh do token
 */
const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token!);
    }
  });
  
  failedQueue = [];
};

/**
 * Realiza refresh do token JWT
 */
const refreshAuthToken = async (): Promise<string> => {
  const refreshToken = authStorage.getRefreshToken();
  
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  try {
    // Usar endpoint em português (padrão do sistema)
    const response = await axios.post('/autenticador/renovar-token', {
      refresh: refreshToken
    });

    const newTokens: AuthTokens = {
      access: response.data.access,
      refresh: response.data.refresh || refreshToken, // Usar novo refresh ou manter o atual
    };

    // Atualizar tokens no storage
    authStorage.updateTokens(newTokens);
    
    console.log('✅ Token JWT renovado com sucesso');
    return newTokens.access;
    
  } catch (error) {
    console.error('❌ Erro ao renovar token JWT:', error);
    
    // Se refresh falhou, limpar dados e redirecionar para login
    authStorage.clearAuthData();
    
    // Disparar evento customizado para componentes reagirem
    window.dispatchEvent(new CustomEvent('auth:token-expired'));
    
    throw error;
  }
};

/**
 * Interceptador de requisições - adiciona token automaticamente
 */
const requestInterceptor = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
  // Atualizar atividade do usuário
  authStorage.updateActivity();
  
  // Adicionar token se disponível
  const token = authStorage.getAccessToken();
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  // API key removida para teste

  return config;
};

/**
 * Interceptador de resposta - trata erros 401 automaticamente
 */
const responseInterceptor = (response: AxiosResponse): AxiosResponse => {
  // Resposta bem-sucedida, apenas retornar
  return response;
};

/**
 * Interceptador de erro - implementa refresh automático
 */
const errorInterceptor = async (error: AxiosError): Promise<any> => {
  const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };
  
  // Verificar se é erro 401 (Unauthorized) e não é uma tentativa de retry
  if (error.response?.status === 401 && !originalRequest._retry) {
    
    // Se já está fazendo refresh, adicionar à fila
    if (isRefreshingToken) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then(token => {
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${token}`;
        }
        return axios(originalRequest);
      }).catch(err => {
        return Promise.reject(err);
      });
    }

    // Marcar que está fazendo refresh
    originalRequest._retry = true;
    isRefreshingToken = true;

    try {
      // Tentar renovar o token
      const newToken = await refreshAuthToken();
      
      // Processar fila de requisições pendentes
      processQueue(null, newToken);
      
      // Atualizar header da requisição original
      if (originalRequest.headers) {
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
      }
      
      // Repetir requisição original
      return axios(originalRequest);
      
    } catch (refreshError) {
      // Falha no refresh - processar fila com erro
      processQueue(refreshError);
      
      // Redirecionar para login se necessário
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
      
      return Promise.reject(refreshError);
      
    } finally {
      isRefreshingToken = false;
    }
  }

  // Para outros tipos de erro, apenas rejeitar
  return Promise.reject(error);
};

/**
 * Configurar interceptadores no Axios
 */
export const setupAuthInterceptors = () => {
  // Limpar interceptadores existentes
  axios.interceptors.request.clear();
  axios.interceptors.response.clear();

  // Configurar interceptador de requisição
  axios.interceptors.request.use(requestInterceptor, (error) => {
    return Promise.reject(error);
  });

  // Configurar interceptador de resposta
  axios.interceptors.response.use(responseInterceptor, errorInterceptor);

  console.log('🔧 Interceptadores de autenticação configurados');
};

/**
 * Verificar se o token está próximo do vencimento
 */
export const isTokenNearExpiry = (token?: string): boolean => {
  if (!token) {
    token = authStorage.getAccessToken();
  }
  
  if (!token) return true;

  try {
    // Decodificar JWT sem verificar assinatura (apenas para ler exp)
    const parts = token.split('.');
    if (parts.length !== 3) return true;
    
    const payload = JSON.parse(atob(parts[1]));
    const expirationTime = payload.exp * 1000; // Converter para milliseconds
    const currentTime = Date.now();
    const timeUntilExpiry = expirationTime - currentTime;
    
    // Considerar "próximo do vencimento" se restam menos de 5 minutos
    return timeUntilExpiry < 5 * 60 * 1000;
    
  } catch (error) {
    console.warn('⚠️ Erro ao verificar expiração do token:', error);
    return true; // Em caso de erro, assumir que está expirado
  }
};

/**
 * Renovar token proativamente se necessário
 */
export const refreshTokenIfNeeded = async (): Promise<void> => {
  if (!authStorage.isLoggedIn()) {
    return;
  }

  const token = authStorage.getAccessToken();
  
  if (isTokenNearExpiry(token)) {
    try {
      await refreshAuthToken();
      console.log('🔄 Token renovado proativamente');
    } catch (error) {
      console.error('❌ Falha na renovação proativa do token:', error);
    }
  }
};

/**
 * Configurar verificação periódica de expiração do token
 */
export const setupTokenRefreshScheduler = () => {
  // Verificar a cada 2 minutos
  const interval = setInterval(() => {
    refreshTokenIfNeeded();
  }, 2 * 60 * 1000);

  // Limpar intervalo quando necessário
  return () => {
    clearInterval(interval);
  };
};

/**
 * Listener para eventos de mudança de foco/visibilidade
 * Renovar token quando usuário volta para a aba
 */
export const setupVisibilityListener = () => {
  const handleVisibilityChange = () => {
    if (!document.hidden && authStorage.isLoggedIn()) {
      refreshTokenIfNeeded();
    }
  };

  document.addEventListener('visibilitychange', handleVisibilityChange);
  
  return () => {
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  };
};